/**
 * Event Handlers - Pure Projection Functions with Transaction Management
 * 
 * ARCHITECTURE PATTERN (CRDT-STYLE):
 * - Events update IndexedDB (server truth)
 * - Handlers use SHARED MUTATIONS from state/mutations.ts
 * - After DB update, memory state is reloaded
 * - Commands use same mutations for optimistic updates
 * 
 * HANDLER RESPONSIBILITIES:
 * - Read current state from IndexedDB transaction
 * - Apply shared mutation functions
 * - Write updated state back to IndexedDB
 * - Declare which stores they need
 * 
 * SEPARATION OF CONCERNS:
 * - Mutations (state/mutations.ts): HOW state changes (pure functions)
 * - Handlers: Apply mutations to IndexedDB (server truth)
 * - Commands: Apply mutations to memory (optimistic)
 * - Processor: Orchestrates transactions, command removal, version updates
 * 
 * TYPE SAFETY & SELF-DESCRIBING HANDLERS:
 * - TypeScript discriminated unions provide automatic type narrowing
 * - HandlerMap enforces that ALL event types have a handler (compile-time check)
 * - Each handler receives its specific event type with typed payload
 * - Each handler declares which stores it needs (self-describing)
 * - If you add a new event type, TypeScript errors until you add its handler + stores
 * 
 * BENEFITS:
 * - Atomicity: All changes (data + command removal + version) in one transaction
 * - No duplication: Mutations shared between commands and events
 * - Server as truth: IndexedDB only modified by events
 * - Optimistic updates: Commands modify memory immediately
 * - Easy to test: Mutations are pure functions
 * 
 * FLOW:
 * 1. Processor opens transaction with all required stores
 * 2. Processor calls projection(event, transaction)
 * 3. Projection applies shared mutations to IndexedDB
 * 4. Processor removes command and updates version
 * 5. Transaction commits atomically
 * 6. Memory state reloads from IndexedDB + pending commands
 */

import type { 
  ScenarioEvent,
  ItemCompletedEvent,
  ItemRenamedEvent,
  ItemCreatedEvent,
  ItemQuantityUpdatedEvent,
  ItemsCreatedEvent,
  ItemDeletedEvent,
  ItemReorderedEvent,
  ListCreatedEvent,
  ListRenamedEvent,
  ListDeletedEvent,
  ScenarioCreatedEvent,
  ScenarioRenamedEvent,
  ScenarioDeletedEvent,
  ScenarioAccessGrantedEvent,  ScenarioAccessRevokedEvent,  ScenarioItemsDeletedEvent,
  ScenarioItemsAddedEvent,
  FlowCreatedEvent,
  FlowRenamedEvent,
  FlowDeletedEvent,
  FlowRankingsUpdatedEvent,
  DefaultFlowSetEvent,
  ItemFlowPreferencesSetEvent
} from './types'

import type { ScenarioItemsCollection } from '../domain/models'
import * as mutations from '../state/mutations'
import type { List } from '../domain/models'
import { logger } from '../utils/logger'

async function getByKey<T>(store: IDBObjectStore, key: IDBValidKey): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const request = store.get(key)
    request.onsuccess = () => resolve((request.result ?? null) as T | null)
    request.onerror = () => resolve(null)
  })
}

/**
 * Helper: Find list containing an item
 * Scans all lists in scenario to find the one containing the item
 */
async function findListWithItem(transaction: IDBTransaction, scenarioId: string, itemId: string): Promise<List | null> {
  const listStore = transaction.objectStore('lists')
  const index = listStore.index('scenarioId')
  
  return new Promise<List | null>((resolve) => {
    const request = index.getAll(scenarioId)
    request.onsuccess = () => {
      const lists = request.result as List[]
      const found = lists.find(l => l.items && l.items[itemId])
      resolve(found || null)
    }
    request.onerror = () => resolve(null)
  })
}



/**
 * Store names that can be used in transactions
 */
export type StoreName = 'scenarios' | 'lists' | 'items' | 'syncQueue' | 'scenarioItems' | 'flows'

/**
 * Handler function signature
 */
export type ProjectionHandler = (event: ScenarioEvent, transaction: IDBTransaction) => Promise<void>

/**
 * Handler descriptor with required stores
 * Each handler declares which stores it needs for its transaction
 */
export interface HandlerDescriptor {
  stores: StoreName[]
  handler: ProjectionHandler
}

/**
 * Project ItemCompleted event
 * Uses shared mutation: applyItemCompletion
 */
export async function projectItemCompleted(event: ItemCompletedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, itemId, isComplete } = event.payload
  
  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)
  
  if (list) {
    const updatedList = mutations.updateItemInList(list, itemId, (item) => 
      mutations.applyItemCompletion(item, isComplete)
    )
    listStore.put(updatedList)
  }
}

/**
 * Project ItemRenamed event
 * Uses shared mutations: applyItemRename + applyScenarioItemRename
 */
export async function projectItemRenamed(event: ItemRenamedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, itemId, newName } = event.payload
  const scenarioId = event.scenarioId
  
  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)
  
  if (list) {
    const updatedList = mutations.updateItemInList(list, itemId, (item) => 
      mutations.applyItemRename(item, newName)
    )
    listStore.put(updatedList)
  }

  // Update scenario items
  const scenarioItemsStore = transaction.objectStore('scenarioItems')
  const currentDoc = await new Promise<ScenarioItemsCollection | null>((resolve) => {
    const request = scenarioItemsStore.get(scenarioId)
    request.onsuccess = () => resolve(request.result ?? null)
    request.onerror = () => resolve(null)
  })

  const currentItems = currentDoc?.items || []
  const updatedItems = mutations.applyScenarioItemRename(currentItems, {
    itemId,
    newName
  })

  if (updatedItems !== currentItems) {
    const newDoc: ScenarioItemsCollection = {
      scenarioId,
      items: updatedItems,
      lastSyncedAt: currentDoc?.lastSyncedAt || Date.now()
    }
    scenarioItemsStore.put(newDoc)
  }
}

/**
 * Project ScenarioItemsDeleted event
 * Uses shared mutation: applyDeleteScenarioItems
 */
export async function projectScenarioItemsDeleted(event: ScenarioItemsDeletedEvent, transaction: IDBTransaction): Promise<void> {
  const { itemIds } = event.payload
  const scenarioId = event.scenarioId
    
  if (transaction.objectStoreNames.contains('scenarioItems')) {
    const scenarioItemsStore = transaction.objectStore('scenarioItems')
    
    // Get current items
    const currentDoc = await new Promise<any>((resolve) => {
      const request = scenarioItemsStore.get(scenarioId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
    })
    
    if (currentDoc) {
      const currentItems = currentDoc.items || []
      const updatedItems = mutations.applyDeleteScenarioItems(currentItems, itemIds)
      
      if (updatedItems.length !== currentItems.length) {
        const updatedDoc: ScenarioItemsCollection = {
            ...currentDoc,
            items: updatedItems,
        }
        scenarioItemsStore.put(updatedDoc)
      }
    }
  }
}

/**
 * Project ScenarioItemsAdded event
 * Adds new items to the scenario's store
 */
export async function projectScenarioItemsAdded(event: ScenarioItemsAddedEvent, transaction: IDBTransaction): Promise<void> {
  const { items } = event.payload
  const scenarioId = event.scenarioId
  const lastUsedAt = event.clientTimestamp || event.serverTimestamp
  const serverSyncedAt = Date.parse(event.serverTimestamp)
    
  if (transaction.objectStoreNames.contains('scenarioItems')) {
    const scenarioItemsStore = transaction.objectStore('scenarioItems')
    
    // Get current items
    const currentDoc = await new Promise<any>((resolve) => {
      const request = scenarioItemsStore.get(scenarioId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
    })
    
    const currentItems = currentDoc?.items || []
    const currentLastSyncedAt = currentDoc?.lastSyncedAt ?? 0
    const eventLastSyncedAt = Number.isFinite(serverSyncedAt) ? serverSyncedAt : currentLastSyncedAt
    
    const newItems = [...currentItems]
    for (const item of items) {
      const id = String(item?.id || '').trim()
      const name = String(item?.name || '').trim()

      // Strict contract: ignore invalid items; no fallback/mapping.
      if (!id || !name) continue

      const existingIndex = newItems.findIndex(s => s.id === id)
      if (existingIndex >= 0) {
        continue
      }

      newItems.push({
        id,
        name,
        quantity: 1,
        usageCount: 0,
        lastUsedAt
      })
    }
    
    const updatedDoc: ScenarioItemsCollection = {
      scenarioId,
      items: newItems,
      lastSyncedAt: Math.max(currentLastSyncedAt, eventLastSyncedAt)
    }
    scenarioItemsStore.put(updatedDoc)
  }
}

/**
 * Project ItemCreated event
 * Uses shared mutation: addItemToList
 */
export async function projectItemCreated(event: ItemCreatedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, itemId, itemName, order, quantity } = event.payload
  const scenarioId = event.scenarioId
  
  // Update scenario items (always track usage)
  const scenarioItemsStore = transaction.objectStore('scenarioItems')
  
  // Get current items
  const currentDoc = await new Promise<ScenarioItemsCollection | null>((resolve) => {
    const request = scenarioItemsStore.get(scenarioId)
    request.onsuccess = () => resolve(request.result ?? null)
    request.onerror = () => resolve(null)
  })
  
  const currentItems = currentDoc?.items || []
  const updatedItems = mutations.applyScenarioItemUsage(currentItems, {
    itemId,
    itemName,
    lastUsedAt: event.clientTimestamp || event.serverTimestamp,
    quantity
  })
  
  const newDoc: ScenarioItemsCollection = {
    scenarioId,
    items: updatedItems,
    lastSyncedAt: currentDoc?.lastSyncedAt || Date.now()
  }
  
  scenarioItemsStore.put(newDoc)

  // Add item to parent list
  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)

  if (list) {
    const newItem = mutations.createItem({
      itemId,
      listId,
      scenarioId,
      itemName,
      quantity,
      sortOrder: order
    })
    
    // Check if item already exists in list (optimistic update) to preserve isCompleted?
    // Embedded items are stored as a dictionary keyed by itemId.
    // If we want to preserve local state like isCompleted, we should check:
    const existingItem = list.items?.[itemId]
    if (existingItem?.isCompleted) {
       newItem.isCompleted = true
    }

    const updatedList = mutations.addItemToList(list, newItem)
    
    listStore.put(updatedList)
  }
}

/**
 * Project ItemQuantityUpdated event
 * Uses shared mutations: applyItemQuantity, applyScenarioItemQuantity
 */
export async function projectItemQuantityUpdated(event: ItemQuantityUpdatedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, itemId, quantity, metaItemUpdated } = event.payload

  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)
  if (!list) return

  const updatedList = mutations.updateItemInList(list, itemId, (item) =>
    mutations.applyItemQuantity(item, quantity)
  )
  listStore.put(updatedList)

  if (metaItemUpdated) {
    const scenarioItemsStore = transaction.objectStore('scenarioItems')
    const currentDoc = await getByKey<ScenarioItemsCollection>(scenarioItemsStore, event.scenarioId)
    if (!currentDoc) return

    const nextItems = mutations.applyScenarioItemQuantity(currentDoc.items, { itemId, quantity })
    if (nextItems !== currentDoc.items) {
      scenarioItemsStore.put({ ...currentDoc, items: nextItems })
    }
  }
}

/**
 * Project ItemsCreated event
 * Uses shared mutation: addItemsToList
 */
export async function projectItemsCreated(event: ItemsCreatedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, items } = event.payload
  const scenarioId = event.scenarioId
  
  // Update items for all created items
  const scenarioItemsStore = transaction.objectStore('scenarioItems')
    
  // Get current items
  const currentDoc = await new Promise<ScenarioItemsCollection | null>((resolve) => {
    const request = scenarioItemsStore.get(scenarioId)
    request.onsuccess = () => resolve(request.result ?? null)
    request.onerror = () => resolve(null)
  })
  
  let currentItems = currentDoc?.items || []
  
  // Apply usage update for each item
  for (const item of items) {
    currentItems = mutations.applyScenarioItemUsage(currentItems, {
      itemId: item.itemId,
      itemName: item.itemName,
      lastUsedAt: event.clientTimestamp || event.serverTimestamp,
      quantity: item.quantity
    })
  }
  
  const newDoc: ScenarioItemsCollection = {
    scenarioId,
    items: currentItems,
    lastSyncedAt: currentDoc?.lastSyncedAt || Date.now()
  }
  
  scenarioItemsStore.put(newDoc)

  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)

  if (!list) {
    logger.warn(`[projectItemsCreated] List ${listId} NOT found in IndexedDB, items will NOT be saved!`)
    return
  }
  
  logger.info(`[projectItemsCreated] Found list ${listId} (${list.name}), processing ${items.length} items`)

  // Create Item objects
  const newItems = items.map(item => ({
    itemId: item.itemId,
    itemName: item.itemName,
    order: item.order,
    quantity: item.quantity
  }))
  
  const createdItems = mutations.createItems({
    items: newItems,
    listId,
    scenarioId
  })

  // We rely on addItemsToList to de-dupe by ID if needed
  const updatedList = mutations.addItemsToList(list, createdItems)
  logger.info(`[projectItemsCreated] Saving updated list with ${Object.keys(updatedList.items || {}).length} items`)
  listStore.put(updatedList)
}

/**
 * Project ItemDeleted event
 */
export async function projectItemDeleted(event: ItemDeletedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, itemId } = event.payload
  
  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)
  
  if (list) {
    const updatedList = mutations.deleteItemFromList(list, itemId)
    listStore.put(updatedList)
  }
}

/**
 * Project ListCreated event
 */
export async function projectListCreated(event: ListCreatedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, listName, description } = event.payload
  
  const listStore = transaction.objectStore('lists')
  
  // Check if list already exists (from optimistic update)
  const existing = await getByKey<List>(listStore, listId)
  
  if (existing) {
    return // Already exists, skip
  }
  
  // Create list using shared mutation
  const newList = mutations.createList({
    listId,
    scenarioId: event.scenarioId,
    listName,
    description
  })
  listStore.put(newList)
}

/**
 * Project ListRenamed event
 */
export async function projectListRenamed(event: ListRenamedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId, newName, newDescription } = event.payload
  
  const listStore = transaction.objectStore('lists')
  const list = await getByKey<List>(listStore, listId)
  
  if (list) {
    const updatedList = mutations.applyListRename(list, newName, newDescription)
    listStore.put(updatedList)
  }
}

/**
 * Project ListDeleted event
 */
export async function projectListDeleted(event: ListDeletedEvent, transaction: IDBTransaction): Promise<void> {
  const { listId } = event.payload
  
  const listStore = transaction.objectStore('lists')
  
  // Delete the list (items are embedded, so they go with it)
  listStore.delete(listId)
}

/**
 * Project ScenarioRenamed event
 */
export async function projectScenarioRenamed(event: ScenarioRenamedEvent, transaction: IDBTransaction): Promise<void> {
  // Handle PascalCase from C# with camelCase fallback
  const payload = event.payload as any
  const newName = payload.NewName || payload.newName
  const newDescription = payload.NewDescription !== undefined ? payload.NewDescription : payload.newDescription
  
  const scenarioStore = transaction.objectStore('scenarios')
  const scenario = await new Promise<any>((resolve) => {
    const request = scenarioStore.get(event.scenarioId)
    request.onsuccess = () => resolve(request.result)
  })
  
  if (scenario) {
    console.log('[EventHandler] Renaming scenario:', { 
      oldName: scenario.name, 
      newName, 
      oldDesc: scenario.description,
      newDescription 
    })
    const updatedScenario = mutations.applyScenarioRename(scenario, newName, newDescription)
    console.log('[EventHandler] Updated scenario:', { 
      name: updatedScenario.name, 
      description: updatedScenario.description 
    })
    scenarioStore.put(updatedScenario)
  }
}

/**
 * Project ScenarioDeleted event
 */
export async function projectScenarioDeleted(event: ScenarioDeletedEvent, transaction: IDBTransaction): Promise<void> {
  const scenarioStore = transaction.objectStore('scenarios')
  const scenario = await new Promise<any>((resolve) => {
    const request = scenarioStore.get(event.scenarioId)
    request.onsuccess = () => resolve(request.result)
  })
  
  if (scenario) {
    const deletedScenario = mutations.applyScenarioDeletion(scenario)
    scenarioStore.put(deletedScenario)
  }
}

/**
 * Project ScenarioCreated event
 */
export async function projectScenarioCreated(event: ScenarioCreatedEvent, transaction: IDBTransaction): Promise<void> {
  const { scenarioName, description } = event.payload
  
  const scenarioStore = transaction.objectStore('scenarios')
  
  // Check if scenario already exists (from optimistic update)
  const existing = await new Promise<any>((resolve) => {
    const request = scenarioStore.get(event.scenarioId)
    request.onsuccess = () => resolve(request.result)
  })
  
  if (existing) {
    return // Already exists, skip
  }
  
  // Create scenario using shared mutation
  const newScenario = mutations.createScenario({
    scenarioId: event.scenarioId,
    scenarioName,
    description,
    role: 'owner'
  })
  scenarioStore.put(newScenario)
}

/**
 * Project ScenarioAccessGranted event
 * Minimal projection: update scenario metadata/role if scenario exists locally.
 * If the scenario is not yet hydrated (seq=-1 or missing), snapshot recovery will populate it.
 */
export async function projectScenarioAccessGranted(event: ScenarioAccessGrantedEvent, transaction: IDBTransaction): Promise<void> {
  const scenarioStore = transaction.objectStore('scenarios')

  const existing = await new Promise<any>((resolve) => {
    const request = scenarioStore.get(event.scenarioId)
    request.onsuccess = () => resolve(request.result)
  })

  if (!existing) {
    return
  }
  const existingRoleCandidate = String(existing.role || 'editor').toLowerCase()
  const role: 'owner' | 'editor' = existingRoleCandidate === 'owner' ? 'owner' : 'editor'

  scenarioStore.put({
    ...existing,
    name: event.payload.scenarioName ?? existing.name,
    description: event.payload.description ?? existing.description,
    role
  })
}

/**
 * Project FlowCreated event
 * Uses shared mutation: createFlow
 */
export async function projectFlowCreated(event: FlowCreatedEvent, transaction: IDBTransaction): Promise<void> {
  const { flowId, flowName, description, color } = event.payload
  
  const flowStore = transaction.objectStore('flows')
  
  const flow = mutations.createFlow({
    flowId,
    scenarioId: event.scenarioId,
    flowName,
    description,
    color
  })
  flowStore.put(flow)
}

/**
 * Project FlowRenamed event
 * Uses shared mutation: applyFlowRename
 */
export async function projectFlowRenamed(event: FlowRenamedEvent, transaction: IDBTransaction): Promise<void> {
  const { flowId, newName, newDescription, newColor } = event.payload
  
  const flowStore = transaction.objectStore('flows')
  const flow = await new Promise<any>((resolve) => {
    const request = flowStore.get(flowId)
    request.onsuccess = () => resolve(request.result)
  })
  
  if (flow) {
    const updatedFlow = mutations.applyFlowRename(flow, newName, newDescription, newColor)
    flowStore.put(updatedFlow)
  }
}

/**
 * Project FlowDeleted event
 * Removes the flow from IndexedDB
 */
export async function projectFlowDeleted(event: FlowDeletedEvent, transaction: IDBTransaction): Promise<void> {
  const { flowId } = event.payload
  const flowStore = transaction.objectStore('flows')
  flowStore.delete(flowId)
}

/**
 * Project FlowRankingsUpdated event
 * Uses shared mutation: applyFlowRankingsUpdate
 */
export async function projectFlowRankingsUpdated(event: FlowRankingsUpdatedEvent, transaction: IDBTransaction): Promise<void> {
  const { flowId, rankings } = event.payload
  
  const flowStore = transaction.objectStore('flows')
  const flow = await new Promise<any>((resolve) => {
    const request = flowStore.get(flowId)
    request.onsuccess = () => resolve(request.result)
  })
  
  if (flow) {
    const updatedFlow = mutations.applyFlowRankingsUpdate(flow, rankings)
    flowStore.put(updatedFlow)
  }
}

/**
 * Project DefaultFlowSet event
 * Sets a flow as default and unsets other defaults, updates scenario header
 */
export async function projectDefaultFlowSet(event: DefaultFlowSetEvent, transaction: IDBTransaction): Promise<void> {
  const { flowId } = event.payload
  const scenarioStore = transaction.objectStore('scenarios')
  
  // Update scenario header with defaultFlowId
  const scenario = await getByKey<any>(scenarioStore, event.scenarioId)
  if (scenario) {
    scenarioStore.put({ ...scenario, defaultFlowId: flowId })
  }
}

/**
 * Project ItemFlowPreferencesSet event
 * Updates multiple flows for a single item's preference status
 */
export async function projectItemFlowPreferencesSet(event: ItemFlowPreferencesSetEvent, transaction: IDBTransaction): Promise<void> {
  const { itemId, flowPreferences } = event.payload
  const flowStore = transaction.objectStore('flows')

  for (const [flowId, pref] of Object.entries(flowPreferences)) {
    const flow = await new Promise<any>((resolve) => {
      const request = flowStore.get(flowId)
      request.onsuccess = () => resolve(request.result)
    })

    if (flow) {
      const existingRanking = flow.rankings?.[itemId] || { r: 0 }
      const ranking = {
        r: existingRanking.r,
        n: existingRanking.n ?? '',
        notAvailable: pref.status === 'notAvailable',
        status: pref.status
      }
      const updatedFlow = mutations.applyFlowRankingsUpdate(flow, { [itemId]: ranking })
      flowStore.put(updatedFlow)
    }
  }
}

/**
 * Type-safe handler registry with store requirements
 * TypeScript enforces that all event types have a handler at compile time
 * Each handler declares which stores it needs - no hardcoded logic elsewhere
 */
type HandlerMap = {
  [K in ScenarioEvent['eventType']]: {
    stores: StoreName[]
    handler: (event: Extract<ScenarioEvent, { eventType: K }>, transaction: IDBTransaction) => Promise<void>
  }
}

/**
 * Handler registry - TypeScript will error if any event type is missing!
 * Each entry explicitly declares ALL stores the handler uses
 * Processor will also add 'syncQueue' store (for command removal infrastructure)
 * Duplicates are automatically deduped by processor
 */
export const handlerRegistry: HandlerMap = {
  ItemCompleted: {
    stores: ['lists'],
    handler: projectItemCompleted
  },
  ItemRenamed: {
    stores: ['lists', 'scenarioItems'],
    handler: projectItemRenamed
  },
  ItemCreated: {
    stores: ['lists', 'scenarioItems'], // Uses lists to embed item
    handler: projectItemCreated
  },
  ItemQuantityUpdated: {
    stores: ['lists', 'scenarioItems'],
    handler: projectItemQuantityUpdated
  },
  ItemsCreated: {
    stores: ['lists', 'scenarioItems'],
    handler: projectItemsCreated
  },
  ItemDeleted: {
    stores: ['lists'],
    handler: projectItemDeleted
  },
  ListCreated: {
    stores: ['lists'],
    handler: projectListCreated
  },
  ListRenamed: {
    stores: ['lists'],
    handler: projectListRenamed
  },
  ListDeleted: {
    stores: ['lists'], // Items are embedded
    handler: projectListDeleted
  },
  ScenarioCreated: {
    stores: ['scenarios'], // Explicitly declare what handler uses
    handler: projectScenarioCreated
  },
  ScenarioAccessGranted: {
    stores: ['scenarios'],
    handler: projectScenarioAccessGranted
  },
  ScenarioRenamed: {
    stores: ['scenarios'], // Explicitly declare what handler uses
    handler: projectScenarioRenamed
  },
  ScenarioDeleted: {
    stores: ['scenarios'], // Explicitly declare what handler uses
    handler: projectScenarioDeleted
  },
  ItemReordered: {
    stores: ['lists'],
    handler: projectItemReordered
  },
  ScenarioItemsDeleted: {
    stores: ['scenarioItems'],
    handler: projectScenarioItemsDeleted
  },
  ScenarioItemsAdded: {
    stores: ['scenarioItems'],
    handler: projectScenarioItemsAdded
  },
  ScenarioAccessRevoked: {
    stores: ['scenarios', 'lists', 'scenarioItems'],
    handler: projectScenarioAccessRevoked
  },
  FlowCreated: {
    stores: ['flows'],
    handler: projectFlowCreated
  },
  FlowRenamed: {
    stores: ['flows'],
    handler: projectFlowRenamed
  },
  FlowDeleted: {
    stores: ['flows'],
    handler: projectFlowDeleted
  },
  FlowRankingsUpdated: {
    stores: ['flows'],
    handler: projectFlowRankingsUpdated
  },
  DefaultFlowSet: {
    stores: ['flows', 'scenarios'],
    handler: projectDefaultFlowSet
  },
  ItemFlowPreferencesSet: {
    stores: ['flows'],
    handler: projectItemFlowPreferencesSet
  }
}

/**
 * Get handler descriptor for an event type
 * Returns both the handler function and required stores
 */
export function getHandlerForEvent(eventType: ScenarioEvent['eventType']) {
  return handlerRegistry[eventType]
}

/**
 * Project ItemReordered event
 * Uses shared mutation: applyItemReorder
 */
export async function projectItemReordered(event: ItemReorderedEvent, transaction: IDBTransaction): Promise<void> {
  const { itemId, newOrder } = event.payload
  const scenarioId = event.scenarioId
  
  const listStore = transaction.objectStore('lists')
  
  // Find list containing the item
  const list = await findListWithItem(transaction, scenarioId, itemId)
  
  if (list) {
    const updatedList = mutations.updateItemInList(list, itemId, (item) => 
      mutations.applyItemReorder(item, newOrder)
    )
    listStore.put(updatedList)
  }
}

/**
 * Project ScenarioAccessRevoked event
 * Removes the scenario metadata if user was removed
 */
export async function projectScenarioAccessRevoked(event: ScenarioAccessRevokedEvent, transaction: IDBTransaction): Promise<void> {
  const { userId } = event.payload
  
  try {
    const cachedUser = localStorage.getItem('auth_user')
    if (cachedUser) {
      const user = JSON.parse(cachedUser)
      if (user && user.userId === userId) {
        // 1. Delete Scenario
        const scenarioStore = transaction.objectStore('scenarios')
        scenarioStore.delete(event.scenarioId)
        
        // 2. Cascading delete for Lists and Items
        // We use getAllKeys on the index to find related records
        const deleteFromStoreByIndex = (storeName: string) => {
          const store = transaction.objectStore(storeName)
          const index = store.index('scenarioId')
          const request = index.getAllKeys(event.scenarioId)
          
          request.onsuccess = () => {
             const keys = request.result
             if (Array.isArray(keys)) {
                keys.forEach((key) => store.delete(key))
             }
          }
        }
        
        deleteFromStoreByIndex('lists')
        // Items are embedded in lists, so no need to delete specific items store (it doesn't exist)

        // 3. Delete scenario items metadata cache
        if (transaction.objectStoreNames.contains('scenarioItems')) {
          transaction.objectStore('scenarioItems').delete(event.scenarioId)
        }
      }
    }
  } catch (e) {
    // Ignore
  }
}


